import { useState, type ReactNode } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Link } from "@corpuscomplete/web/components/link";
import { Page, PageBody, PageContent, PageHeader } from "@corpuscomplete/web/components/layout";
import { Button } from "@corpuscomplete/web-ui/components/button";
import { Badge } from "@corpuscomplete/web-ui/components/badge";
import {
  Empty,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  EmptyDescription,
} from "@corpuscomplete/web-ui/components/empty";
import {
  Dialog,
  DialogTrigger,
  DialogHeader,
  DialogContent,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "@corpuscomplete/web-ui/components/dialog";
import {
  ContextMenu,
  ContextMenuTrigger,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
} from "@corpuscomplete/web-ui/components/context-menu";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@corpuscomplete/web-ui/components/dropdown-menu";
import { Input } from "@corpuscomplete/web-ui/components/input";
import { Label } from "@corpuscomplete/web-ui/components/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@corpuscomplete/web-ui/components/table";
import { Tabs, TabsList, TabsTrigger, TabsPanel } from "@corpuscomplete/web-ui/components/tabs";
import { Text } from "@corpuscomplete/web-ui/components/text";
import { FolderIcon, ChevronDownIcon } from "lucide-react";
import { useAuth } from "@corpuscomplete/web/auth/hooks";
import { ListCollectionList } from "@corpuscomplete/core-ui/common/list-collection/list";
import { FoodCollectionList } from "@corpuscomplete/core-ui/nutrition/food-collection/list";
import { createId } from "@corpuscomplete/core/utils";
import { ScopeBreadcrumb } from "@corpuscomplete/web/modules/common/components/scope-breadcrumb";
import { Scope } from "@corpuscomplete/core/common/scope";

type CollectionType = "food" | "list";

/**
 * Collections route page for food/list collection management.
 * Contract: composes core-ui collection providers and keeps this layer
 * focused on tab/dialog presentation and route navigation links.
 */
export function CollectionsPage({ scope }: { scope: Scope.Info }) {
  const { userId } = useAuth();
  const navigate = useNavigate();
  const scopeId = Scope.id(scope);
  const [activeTab, setActiveTab] = useState<CollectionType>("food");
  const showListCollections = scope.kind === "personal";

  return (
    <FoodCollectionList.DataProvider scopeId={scopeId} userId={userId}>
      <ListCollectionList.DataProvider userId={userId}>
        <Page>
          <PageBody>
            <PageHeader>
              <div className="mr-auto min-w-0">
                <ScopeBreadcrumb
                  crumbs={[{ label: "Collections", current: true }]}
                  scope={scope}
                  scopeBehavior="switchable"
                  onScopeChange={(nextScopeId) => {
                    setActiveTab("food");
                    return nextScopeId === userId
                      ? void navigate({ to: "/personal/collections" })
                      : void navigate({
                          to: "/spaces/$spaceId/collections",
                          params: { spaceId: nextScopeId },
                        });
                  }}
                />
              </div>
              <CreateCollectionButton
                allowListCollections={showListCollections}
                defaultType={showListCollections ? activeTab : "food"}
              />
            </PageHeader>
            <PageContent>
              <Tabs
                value={activeTab}
                onValueChange={(value) => setActiveTab(value as CollectionType)}
              >
                <TabsList>
                  <TabsTrigger value="food">Food Collections</TabsTrigger>
                  {showListCollections ? (
                    <TabsTrigger value="list">List Collections</TabsTrigger>
                  ) : null}
                </TabsList>
                <TabsPanel value="food">
                  <FoodCollectionsTab scope={scope} />
                </TabsPanel>
                {showListCollections ? (
                  <TabsPanel value="list">
                    <ListCollectionsTab />
                  </TabsPanel>
                ) : null}
              </Tabs>
            </PageContent>
          </PageBody>
        </Page>
      </ListCollectionList.DataProvider>
    </FoodCollectionList.DataProvider>
  );
}

function FoodCollectionsTab({ scope }: { scope: Scope.Info }) {
  const scopeId = Scope.id(scope);
  const collections = FoodCollectionList.useCollections().filter(
    (collection) => collection.scopeId === scopeId,
  );
  const { isLoading } = FoodCollectionList.useLoadingState();
  const getPermissions = FoodCollectionList.useCollectionPermissions();
  const [renameCollection, setRenameCollection] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [deleteCollection, setDeleteCollection] = useState<{
    id: string;
    name: string;
    isDefault: boolean;
  } | null>(null);

  if (isLoading && collections.length === 0) {
    return null;
  }

  if (collections.length === 0) {
    return (
      <Empty className="flex-1 py-12">
        <EmptyHeader>
          <EmptyMedia>
            <div className="flex size-16 items-center justify-center rounded-full bg-muted">
              <FolderIcon className="size-8 text-muted-foreground" />
            </div>
          </EmptyMedia>
          <EmptyTitle>No food collections</EmptyTitle>
          <EmptyDescription>Create a collection to organize your recipes.</EmptyDescription>
        </EmptyHeader>
        <CreateCollectionButton allowListCollections={false} defaultType="food" />
      </Empty>
    );
  }

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Role</TableHead>
            <TableHead>Items</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {collections.map((collection) => {
            const { canRename, canDelete } = getPermissions(collection);

            return (
              <ContextMenu key={collection.id}>
                <ContextMenuTrigger>
                  <TableRow className="cursor-pointer">
                    <TableCell>
                      <FoodCollectionLink
                        id={collection.id}
                        className="flex items-center gap-2 hover:underline"
                      >
                        <Text variant="sm" className="font-medium">
                          {collection.name}
                        </Text>
                        {collection.isDefault && <Badge variant="secondary">Default</Badge>}
                      </FoodCollectionLink>
                    </TableCell>
                    <TableCell>
                      <Text variant="sm" className="text-muted-foreground capitalize">
                        {collection.type}
                      </Text>
                    </TableCell>
                    <TableCell>
                      <Text variant="sm" className="text-muted-foreground capitalize">
                        {collection.role}
                      </Text>
                    </TableCell>
                    <TableCell>
                      <Text variant="sm" className="text-muted-foreground">
                        {collection.foodCount}
                      </Text>
                    </TableCell>
                  </TableRow>
                </ContextMenuTrigger>
                <ContextMenuContent>
                  {canRename && (
                    <ContextMenuItem
                      onClick={() =>
                        setRenameCollection({ id: collection.id, name: collection.name })
                      }
                    >
                      <Text variant="sm">Rename</Text>
                    </ContextMenuItem>
                  )}
                  {canDelete && (
                    <>
                      {canRename && <ContextMenuSeparator />}
                      <ContextMenuItem
                        variant="destructive"
                        onClick={() =>
                          setDeleteCollection({
                            id: collection.id,
                            name: collection.name,
                            isDefault: collection.isDefault,
                          })
                        }
                      >
                        <Text variant="sm">Delete</Text>
                      </ContextMenuItem>
                    </>
                  )}
                  {!canRename && !canDelete && (
                    <ContextMenuItem disabled>
                      <Text variant="sm" className="text-muted-foreground">
                        No actions available
                      </Text>
                    </ContextMenuItem>
                  )}
                </ContextMenuContent>
              </ContextMenu>
            );
          })}
        </TableBody>
      </Table>

      {renameCollection && (
        <RenameCollectionDialog
          type="food"
          collection={renameCollection}
          open={true}
          onOpenChange={(open) => {
            if (!open) setRenameCollection(null);
          }}
        />
      )}

      {deleteCollection && (
        <DeleteCollectionDialog
          type="food"
          collection={deleteCollection}
          open={true}
          onOpenChange={(open) => {
            if (!open) setDeleteCollection(null);
          }}
        />
      )}
    </>
  );
}

function FoodCollectionLink({
  children,
  className,
  id,
}: {
  children: ReactNode;
  className?: string;
  id: string;
}) {
  return (
    <Link to="/collections/food/$id" params={{ id }} className={className}>
      {children}
    </Link>
  );
}

function ListCollectionsTab() {
  const collections = ListCollectionList.useCollections();
  const { isLoading } = ListCollectionList.useLoadingState();
  const getPermissions = ListCollectionList.useCollectionPermissions();
  const [renameCollection, setRenameCollection] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [deleteCollection, setDeleteCollection] = useState<{
    id: string;
    name: string;
    isDefault: boolean;
  } | null>(null);

  if (isLoading && collections.length === 0) {
    return null;
  }

  if (collections.length === 0) {
    return (
      <Empty className="flex-1 py-12">
        <EmptyHeader>
          <EmptyMedia>
            <div className="flex size-16 items-center justify-center rounded-full bg-muted">
              <FolderIcon className="size-8 text-muted-foreground" />
            </div>
          </EmptyMedia>
          <EmptyTitle>No list collections</EmptyTitle>
          <EmptyDescription>Create a collection to organize your lists.</EmptyDescription>
        </EmptyHeader>
        <CreateCollectionButton allowListCollections defaultType="list" />
      </Empty>
    );
  }

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Role</TableHead>
            <TableHead>Items</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {collections.map((collection) => {
            const { canRename, canDelete } = getPermissions(collection);

            return (
              <ContextMenu key={collection.id}>
                <ContextMenuTrigger>
                  <TableRow className="cursor-pointer">
                    <TableCell>
                      <Link
                        to="/personal/collections/list/$id"
                        params={{ id: collection.id }}
                        className="flex items-center gap-2 hover:underline"
                      >
                        <Text variant="sm" className="font-medium">
                          {collection.name}
                        </Text>
                        {collection.isDefault && <Badge variant="secondary">Default</Badge>}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Text variant="sm" className="text-muted-foreground capitalize">
                        {collection.role}
                      </Text>
                    </TableCell>
                    <TableCell>
                      <Text variant="sm" className="text-muted-foreground">
                        {collection.listCount}
                      </Text>
                    </TableCell>
                  </TableRow>
                </ContextMenuTrigger>
                <ContextMenuContent>
                  {canRename && (
                    <ContextMenuItem
                      onClick={() =>
                        setRenameCollection({ id: collection.id, name: collection.name })
                      }
                    >
                      <Text variant="sm">Rename</Text>
                    </ContextMenuItem>
                  )}
                  {canDelete && (
                    <>
                      {canRename && <ContextMenuSeparator />}
                      <ContextMenuItem
                        variant="destructive"
                        onClick={() =>
                          setDeleteCollection({
                            id: collection.id,
                            name: collection.name,
                            isDefault: collection.isDefault,
                          })
                        }
                      >
                        <Text variant="sm">Delete</Text>
                      </ContextMenuItem>
                    </>
                  )}
                  {!canRename && !canDelete && (
                    <ContextMenuItem disabled>
                      <Text variant="sm" className="text-muted-foreground">
                        No actions available
                      </Text>
                    </ContextMenuItem>
                  )}
                </ContextMenuContent>
              </ContextMenu>
            );
          })}
        </TableBody>
      </Table>

      {renameCollection && (
        <RenameCollectionDialog
          type="list"
          collection={renameCollection}
          open={true}
          onOpenChange={(open) => {
            if (!open) setRenameCollection(null);
          }}
        />
      )}

      {deleteCollection && (
        <DeleteCollectionDialog
          type="list"
          collection={deleteCollection}
          open={true}
          onOpenChange={(open) => {
            if (!open) setDeleteCollection(null);
          }}
        />
      )}
    </>
  );
}

function CreateCollectionButton({
  allowListCollections,
  defaultType,
}: {
  allowListCollections: boolean;
  defaultType: CollectionType;
}) {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState<CollectionType>(defaultType);
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const foodMutations = FoodCollectionList.useMutations();
  const listMutations = ListCollectionList.useMutations();

  const handleCreate = () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError("Name is required");
      return;
    }
    if (trimmedName.length > 100) {
      setError("Name must be 100 characters or less");
      return;
    }

    const id = createId();
    if (type === "food") {
      foodMutations.create({ id, name: trimmedName });
    } else {
      if (!allowListCollections) {
        setError("List collections are only available in personal collections.");
        return;
      }
      listMutations.create({ id, name: trimmedName });
    }

    setOpen(false);
    setName("");
    setError(null);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        setOpen(isOpen);
        if (!isOpen) {
          setName("");
          setError(null);
        } else {
          setType(allowListCollections ? defaultType : "food");
        }
      }}
    >
      <DialogTrigger render={<Button variant="outline" size="sm" />}>
        Create Collection
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create Collection</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4 py-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="collection-type">Type</Label>
            <DropdownMenu>
              <DropdownMenuTrigger
                render={<Button variant="outline" className="justify-between" />}
              >
                <Text variant="sm">{type === "food" ? "Food Collection" : "List Collection"}</Text>
                <ChevronDownIcon className="size-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={() => setType("food")}>
                  <Text variant="sm">Food Collection</Text>
                </DropdownMenuItem>
                {allowListCollections ? (
                  <DropdownMenuItem onClick={() => setType("list")}>
                    <Text variant="sm">List Collection</Text>
                  </DropdownMenuItem>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="collection-name">Name</Label>
            <Input
              id="collection-name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setError(null);
              }}
              placeholder="Enter collection name"
              maxLength={100}
            />
            {error && (
              <Text variant="sm" className="text-destructive">
                {error}
              </Text>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleCreate}>
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RenameCollectionDialog({
  type,
  collection,
  open,
  onOpenChange,
}: {
  type: CollectionType;
  collection: { id: string; name: string };
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [name, setName] = useState(collection.name);
  const [error, setError] = useState<string | null>(null);
  const foodMutations = FoodCollectionList.useMutations();
  const listMutations = ListCollectionList.useMutations();

  const handleRename = () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError("Name is required");
      return;
    }
    if (trimmedName.length > 100) {
      setError("Name must be 100 characters or less");
      return;
    }

    if (type === "food") {
      foodMutations.rename(collection.id, trimmedName);
    } else {
      listMutations.rename(collection.id, trimmedName);
    }

    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Rename Collection</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4 py-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="rename-collection-name">Name</Label>
            <Input
              id="rename-collection-name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setError(null);
              }}
              placeholder="Enter collection name"
              maxLength={100}
            />
            {error && (
              <Text variant="sm" className="text-destructive">
                {error}
              </Text>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleRename}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeleteCollectionDialog({
  type,
  collection,
  open,
  onOpenChange,
}: {
  type: CollectionType;
  collection: { id: string; name: string; isDefault: boolean };
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const foodMutations = FoodCollectionList.useMutations();
  const listMutations = ListCollectionList.useMutations();

  const handleDelete = async () => {
    try {
      const { client } =
        type === "food" ? foodMutations.del(collection.id) : listMutations.del(collection.id);

      const result = await client;
      if (result.type === "error") {
        setError(result.error.message);
        return;
      }
      onOpenChange(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to delete collection");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete Collection?</DialogTitle>
          <DialogDescription>
            Are you sure you want to delete "{collection.name}"? This action cannot be undone.
          </DialogDescription>
        </DialogHeader>
        {error && (
          <div className="py-2">
            <Text variant="sm" className="text-destructive">
              {error}
            </Text>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="destructive" size="sm" onClick={handleDelete}>
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
